import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { createSelector, createSlice } from '@reduxjs/toolkit';

const selectTableInfo = (state) => state.tableInfo;
const selectTableId = (state) => state.tableInfo.id;
const selectTableTotalRows = (state) => state.tableInfo.numRows;
const selectTableMissingValues = (state) => state.tableInfo.missingValues;
const selectTableTotalColumns = (state) => state.tableInfo.numColumns;
const selectTableInfoIsLoading = (state) => state.tableInfo.isLoading;
const selectTableColumns = (state) => state.tableInfo.columns;
const selectTableColumnIds = (state) => state.tableInfo.columnIds;
const selectTableColumnsMap = (state) => state.tableInfo.columnsMap;
const selectTableColumnIndexes = (state) => state.tableInfo.columnIndexes;
const selectTableScoringTemplateId = (state) => { var _a; return (_a = state.tableInfo.scoringTemplateId) === null || _a === void 0 ? void 0 : _a.id; };
const selectTableComparisonScoringTemplateId = (state) => { var _a; return (_a = state.tableInfo.comparisonScoringTemplateId) === null || _a === void 0 ? void 0 : _a.id; };
const selectIsTemplatesComparisonMode = (state) => state.tableInfo.isTemplatesComparisonMode;
const selectSiderWidth = createSelector(selectTableInfo, (tableInfo) => tableInfo.siderWidth);

function useTableColumnInfo(colId) {
    const selector = useCallback((state) => selectTableInfo(state).columnsMap[colId], [colId]);
    return useSelector(selector);
}

function toColumnMetaInfo(fields) {
    return fields.map((field) => {
        var _a, _b;
        const histogramMax = Math.max(...(((_a = field.statistics) === null || _a === void 0 ? void 0 : _a.histogram) || [])) || 1; // todo temporary fix, wait for BE
        let histogramBars = [];
        let dataWidth = 0;
        const isDiscreteColumn = ((_b = field.statistics) === null || _b === void 0 ? void 0 : _b.textCategories) !== null;
        if (field.statistics && field.statistics.histogram) {
            const min = field.statistics.min;
            const max = field.statistics.max;
            const step = field.statistics.histogramStep || 1; // todo temporary fix, wait for BE
            dataWidth = max - min;
            histogramBars = field.statistics.histogram.map((yVal, index, arr) => {
                const bar = {
                    x0: min + step * index,
                    x: index === arr.length - 1 ? max : min + step * (index + 1),
                    y0: 0,
                    y: arr.length !== 0 ? yVal : yVal || 1, // todo temporary fix, wait for BE
                };
                return bar;
            });
        }
        const meta = Object.assign(Object.assign({}, field), { histogramMax,
            histogramBars,
            dataWidth,
            isDiscreteColumn });
        return meta;
    });
}

const DEFAULT_SIDER_WIDTH = 355;
const MAX_SIDER_WIDTH = DEFAULT_SIDER_WIDTH * 2;
const initialState = {
    id: '',
    name: '',
    isLoading: false,
    tableDataSize: 0,
    numRows: 0,
    numColumns: 0,
    columns: [],
    columnsMap: {},
    columnIds: [],
    columnIndexes: {},
    scoringTemplateId: null,
    comparisonScoringTemplateId: null,
    isTemplatesComparisonMode: false,
    missingValues: 0,
    siderWidth: DEFAULT_SIDER_WIDTH,
};
const tableInfoSlice = createSlice({
    name: 'tableInfo',
    initialState,
    reducers: {
        initTableInfo(state, action) {
            const { data } = action.payload;
            state.id = data._id;
            state.name = data.fileName;
            state.tableDataSize = data.fileSize;
            state.numRows = data.rowCount;
            state.numColumns = data.fields.length;
            state.columns = toColumnMetaInfo(data.fields);
            state.columnIds = data.fields.map((field) => field.name);
            state.comparisonScoringTemplateId = null;
            state.isTemplatesComparisonMode = false;
            state.columnsMap = state.columns.reduce((acc, col) => {
                acc[col.name] = col;
                return acc;
            }, {});
            state.columnIndexes = state.columns.reduce((acc, col, index) => {
                acc[col.name] = index;
                return acc;
            }, {});
            // TODO need to discuss
            state.scoringTemplateId = {
                parentId: data.scoringTemplateId,
                id: data.scoringTemplateId,
            };
            state.missingValues = data.missingValues;
        },
        toggleTableInfoLoading(state, action) {
            state.isLoading = action.payload;
        },
        setIsTemplatesComparisonMode(state, action) {
            state.isTemplatesComparisonMode = action.payload;
        },
        setTableScoringTemplateId(state, action) {
            state.scoringTemplateId = action.payload
                ? { parentId: action.payload.parentId, id: action.payload.id }
                : null;
        },
        setTableComparisonScoringTemplateId(state, action) {
            state.comparisonScoringTemplateId = action.payload
                ? { parentId: action.payload.parentId, id: action.payload.id }
                : null;
        },
        setSiderWidth(state, action) {
            let width = action.payload;
            if (width > MAX_SIDER_WIDTH) {
                width = MAX_SIDER_WIDTH;
            }
            else if (width < DEFAULT_SIDER_WIDTH) {
                width = DEFAULT_SIDER_WIDTH;
            }
            state.siderWidth = width;
        },
    },
});
const { initTableInfo, toggleTableInfoLoading, setTableScoringTemplateId, setTableComparisonScoringTemplateId, setIsTemplatesComparisonMode, setSiderWidth, } = tableInfoSlice.actions;

export { DEFAULT_SIDER_WIDTH, MAX_SIDER_WIDTH, initTableInfo, selectIsTemplatesComparisonMode, selectSiderWidth, selectTableColumnIds, selectTableColumnIndexes, selectTableColumns, selectTableColumnsMap, selectTableComparisonScoringTemplateId, selectTableId, selectTableInfo, selectTableInfoIsLoading, selectTableMissingValues, selectTableScoringTemplateId, selectTableTotalColumns, selectTableTotalRows, setIsTemplatesComparisonMode, setSiderWidth, setTableComparisonScoringTemplateId, setTableScoringTemplateId, tableInfoSlice, toColumnMetaInfo, toggleTableInfoLoading, useTableColumnInfo };
//# sourceMappingURL=index.js.map
